// NotifyMeModal.jsx
// ----------------------------------------------------------------------
// Captures "Notify me when available" leads when a customer's pickup
// location falls outside every active PickNGo hub's service radius.
// Posts to POST /api/serviceability/notify-me (public; no auth needed,
// but the backend attaches user_id if a session is present).
//
// Props:
//   open       — boolean
//   onClose    — () => void
//   prefill    — { name?, phone?, email?, requested_city?,
//                  pickup_pincode?, pickup_address?, pickup_lat?,
//                  pickup_lng? }
import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Loader2, MapPin, BellRing } from "lucide-react";
import { toast } from "sonner";
import api from "../services/api";

const PHONE_RE = /^\d{10}$/;

export default function NotifyMeModal({ open, onClose, prefill = {} }) {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    requested_city: "",
    pickup_pincode: "",
    pickup_address: "",
    pickup_lat: null,
    pickup_lng: null,
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  // Re-prefill every time the modal opens (covers prop changes too).
  useEffect(() => {
    if (open) {
      setDone(false);
      setForm((f) => ({
        ...f,
        name: prefill.name ?? f.name ?? "",
        phone: prefill.phone ?? f.phone ?? "",
        email: prefill.email ?? f.email ?? "",
        requested_city: prefill.requested_city ?? "",
        pickup_pincode: prefill.pickup_pincode ?? "",
        pickup_address: prefill.pickup_address ?? "",
        pickup_lat: prefill.pickup_lat ?? null,
        pickup_lng: prefill.pickup_lng ?? null,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const valid =
    form.name.trim().length >= 2 &&
    PHONE_RE.test(form.phone.trim());

  const submit = async () => {
    if (!valid) {
      toast.error("Please enter your name and a valid 10-digit phone number.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/serviceability/notify-me", {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim() || null,
        requested_city: form.requested_city.trim() || null,
        pickup_pincode: form.pickup_pincode.trim() || null,
        pickup_address: form.pickup_address.trim() || null,
        pickup_lat: form.pickup_lat,
        pickup_lng: form.pickup_lng,
      });
      toast.success(data?.message || "You're on our waitlist — we'll be in touch!");
      setDone(true);
      setTimeout(() => {
        setSubmitting(false);
        onClose?.();
      }, 1500);
    } catch (e) {
      setSubmitting(false);
      toast.error(e?.response?.data?.detail || "Could not save your request. Please try again.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BellRing className="h-5 w-5 text-orange-500" /> Notify me when available
          </DialogTitle>
          <DialogDescription>
            We're working hard to expand PickNGo. Leave your details and we'll text you
            the day pickups go live in your area.
          </DialogDescription>
        </DialogHeader>

        {done ? (
          <div className="py-8 text-center space-y-2">
            <BellRing className="h-10 w-10 text-emerald-500 mx-auto" />
            <p className="font-medium">You're on the waitlist!</p>
            <p className="text-sm text-muted-foreground">We'll be in touch when we expand to your area.</p>
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <Label htmlFor="nm-name">Full name <span className="text-red-500">*</span></Label>
              <Input id="nm-name" data-testid="notify-me-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Aman Sharma" />
            </div>
            <div>
              <Label htmlFor="nm-phone">Phone (10-digit) <span className="text-red-500">*</span></Label>
              <Input id="nm-phone" data-testid="notify-me-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, "").slice(0, 10) })} placeholder="9876543210" inputMode="numeric" />
            </div>
            <div>
              <Label htmlFor="nm-email">Email (optional)</Label>
              <Input id="nm-email" data-testid="notify-me-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label htmlFor="nm-city">City</Label>
                <Input id="nm-city" data-testid="notify-me-city" value={form.requested_city} onChange={(e) => setForm({ ...form, requested_city: e.target.value })} placeholder="Jodhpur" />
              </div>
              <div>
                <Label htmlFor="nm-pin">Pincode</Label>
                <Input id="nm-pin" data-testid="notify-me-pincode" value={form.pickup_pincode} onChange={(e) => setForm({ ...form, pickup_pincode: e.target.value.replace(/\D/g, "").slice(0, 6) })} placeholder="342001" inputMode="numeric" />
              </div>
            </div>
            {form.pickup_address && (
              <div className="text-xs text-muted-foreground flex items-start gap-1.5 mt-1">
                <MapPin className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                <span className="line-clamp-2">{form.pickup_address}</span>
              </div>
            )}
          </div>
        )}

        {!done && (
          <DialogFooter>
            <Button variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
            <Button onClick={submit} disabled={submitting || !valid} data-testid="notify-me-submit" className="bg-orange-500 hover:bg-orange-600">
              {submitting ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Submitting…</> : "Notify me"}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
